import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";

import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bem } from "@react-md/utils";
import Input, { InputProps, InputRef } from "antd/es/input/Input";
import cn from "classnames";

import { isDefined } from "@mapmycustomers/shared/util/assert";

import { useConfigProvider } from "../../../ConfigProvider";
import Footer from "../Footer";
import InlineLabeled, { InlineLabeledProps } from "../InlineLabeled";

export interface InlineTextFieldProps
  extends Omit<InputProps, "onChange">, Omit<InlineLabeledProps, "children"> {
  caption?: string;
  defaultEditing?: boolean;
  disabled?: boolean;
  editing?: boolean;
  onCancel?: () => void;
  onChange?: (value: string, event?: ChangeEvent<HTMLInputElement>) => void;
  onEditingChange?: (editing: boolean) => void;
  showFooterButtons?: boolean;
  value?: string;
}

const block = bem("mmc-single-line-text-field");

const InlineTextField = ({
  addonBefore,
  caption,
  className,
  defaultEditing = false,
  disabled,
  editing,
  label,
  labelClassName,
  labelPosition = "side",
  onCancel,
  onChange,
  onEditingChange,
  placeholder,
  required,
  showFooterButtons = true,
  suffix,
  value,
  ...props
}: InlineTextFieldProps) => {
  const configProvider = useConfigProvider();
  const [inlineValue, setInlineValue] = useState<string | undefined>(value);

  const [internalEditing, setInternalEditing] = useState(defaultEditing);
  const isEditingControlled = isDefined(editing);
  const isEditing = isEditingControlled ? editing : internalEditing;

  const requestEditingChange = useCallback(
    (next: boolean) => {
      onEditingChange?.(next);
      if (!isEditingControlled) {
        setInternalEditing(next);
      }
    },
    [isEditingControlled, onEditingChange]
  );

  // Keep local value fresh while controlled (the field never collapses, so it
  // must reflect external resets: group Cancel, Find Me, country→region clear).
  useEffect(() => {
    if (isEditingControlled) {
      setInlineValue(value);
    }
  }, [isEditingControlled, value]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setInlineValue(event.target.value),
    []
  );

  const handleSave = useCallback(() => {
    onChange?.(inlineValue ?? "");
    requestEditingChange(false);
  }, [inlineValue, requestEditingChange, onChange]);

  const handleCancel = useCallback(() => {
    onCancel?.();
    requestEditingChange(false);
  }, [requestEditingChange, onCancel]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        handleCancel();
      } else if (event.key === "Enter") {
        event.stopPropagation();
        handleSave();
      }
    },
    [handleCancel, handleSave]
  );

  const inputRef = useRef<InputRef>(null);
  const handleStartEditing = useCallback(() => {
    if (disabled) {
      return;
    }
    setInlineValue(value);
    requestEditingChange(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, [disabled, requestEditingChange, value]);

  const handleBlur = useCallback<NonNullable<InputProps["onBlur"]>>(
    (e) => {
      // To prevent blur action when cancel button is clicked as we save text onblur
      if (e.relatedTarget && e.relatedTarget.classList.contains("mmc-js-skip-save-on-blur")) {
        e.preventDefault();
        handleCancel();
        return;
      }
      handleSave();
    },
    [handleCancel, handleSave]
  );

  const content = isEditing ? (
    <Input
      allowClear
      autoFocus={!isEditingControlled}
      className={block("input")}
      disabled={disabled}
      onBlur={handleBlur}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      ref={inputRef}
      required={required}
      size="large"
      suffix={suffix}
      value={inlineValue}
      {...props}
    />
  ) : (
    <div className={block("value")} onClick={handleStartEditing}>
      {value ?? (
        <span className={block("placeholder")}>
          {placeholder ?? configProvider.formatMessage("ui.inlineInput.unknown")}
        </span>
      )}
    </div>
  );

  return (
    <div className={cn(block({ disabled: !!disabled }), className)}>
      {label ? (
        <InlineLabeled
          extra={disabled ? <FontAwesomeIcon className={block("lock")} icon={faLock} /> : undefined}
          label={
            <div className={block("label-item")}>
              {addonBefore}
              {label}
            </div>
          }
          labelClassName={labelClassName}
          labelPosition={labelPosition}
          required={required}
        >
          {content}
        </InlineLabeled>
      ) : (
        content
      )}
      {showFooterButtons && (
        <Footer
          caption={caption}
          disabled={disabled}
          editing={isEditing}
          labeled={!!label}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default InlineTextField;
